import React, { useEffect, useState } from "react";
import { fechaHoraMX } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, Trash2, ChevronDown, ChevronRight, Inbox, FileText, Eye, Download, CreditCard, AlertTriangle } from "lucide-react";

const ESTATUS_LABEL = { pendiente: "Pendiente", aprobada: "Aprobada", rechazada: "Rechazada" };
const ESTATUS_VARIANT = { pendiente: "default", aprobada: "secondary", rechazada: "destructive" };
const TIPO_LABEL = { contado: "Contado", credito: "Crédito", contado_credito: "Contado / Crédito" };
const fmtFecha = (iso) => fechaHoraMX(iso);
const m = (v) => "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Documentos requeridos según tipo de operación + tipo de persona (solo advertencia).
const faltantesDocs = (s, sdocs) => {
  const req = ["CSF", "Comprobante de domicilio"];
  if (s.solicita_credito) {
    req.push("INE del titular / representante", "INE del aval", "Comprobante de domicilio del aval");
    if (s.tipo_persona === "moral") req.push("Acta constitutiva", "Poder del representante legal");
  }
  return req.filter((t) => !(sdocs || []).some((d) => d.tipo_documento === t));
};

const Detalle = ({ label, value }) => (
  <div className="flex text-sm border-b border-border/60 py-1">
    <span className="w-48 shrink-0 font-medium text-muted-foreground">{label}</span>
    <span className="flex-1 break-words">{value || "—"}</span>
  </div>
);

export default function SolicitudesCliente() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pendiente");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [docsById, setDocsById] = useState({});
  const [rechazar, setRechazar] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    setLoading(true);
    api.get("/solicitudes", { params: { estatus: tab } })
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("No se pudieron cargar las solicitudes"))
      .finally(() => setLoading(false));
  };
  useEffect(cargar, [tab]);

  const toggle = async (s) => {
    if (expandido === s.id) { setExpandido(null); return; }
    setExpandido(s.id);
    if (!docsById[s.id]) {
      try {
        const { data } = await api.get("/documentos", { params: { entidad_tipo: "solicitud", entidad_id: s.id } });
        setDocsById((p) => ({ ...p, [s.id]: data || [] }));
      } catch { setDocsById((p) => ({ ...p, [s.id]: [] })); }
    }
  };

  const abrirBlob = async (doc, descargar = false) => {
    try {
      const { data } = await api.get(`/documentos/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (descargar) { const a = document.createElement("a"); a.href = url; a.download = doc.nombre_archivo; document.body.appendChild(a); a.click(); a.remove(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No se pudo abrir el documento."); }
  };

  const confirmarRechazo = async () => {
    if (!rechazar) return;
    setBusy(true);
    try {
      await api.post(`/solicitudes/${rechazar.id}/rechazar`, { motivo: motivo.trim() || null });
      toast.success("Solicitud rechazada.");
      setRechazar(null); setMotivo(""); cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar la solicitud"); }
    finally { setBusy(false); }
  };

  const eliminar = async (s) => {
    if (!window.confirm("¿Eliminar esta solicitud del historial?")) return;
    try { await api.delete(`/solicitudes/${s.id}`); toast.success("Solicitud eliminada."); cargar(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="solicitudes-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Solicitudes de alta de cliente</h1>
        <p className="text-muted-foreground mt-2">Revisa las solicitudes y sus documentos, y aprueba para crear el cliente automáticamente.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="solicitudes-tabs">
          <TabsTrigger value="pendiente" data-testid="tab-pendiente">Pendientes</TabsTrigger>
          <TabsTrigger value="aprobada" data-testid="tab-aprobada">Aprobadas</TabsTrigger>
          <TabsTrigger value="rechazada" data-testid="tab-rechazada">Rechazadas</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground flex flex-col items-center gap-3" data-testid="solicitudes-vacio">
          <Inbox className="h-10 w-10 opacity-40" />
          <p>No hay solicitudes {ESTATUS_LABEL[tab]?.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const abierto = expandido === s.id;
            const sdocs = docsById[s.id] || [];
            return (
              <Card key={s.id} data-testid={`solicitud-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <button className="flex items-start gap-2 text-left" onClick={() => toggle(s)} data-testid={`solicitud-toggle-${s.id}`}>
                      {abierto ? <ChevronDown className="h-4 w-4 mt-1 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-1 shrink-0" />}
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {s.nombre_razon_social}
                          {s.tipo_operacion && (
                            <Badge variant="outline" className={s.solicita_credito ? "border-amber-500 text-amber-600" : "border-slate-400 text-slate-600"}>
                              {s.solicita_credito && <CreditCard className="h-3 w-3 mr-1" />}
                              {TIPO_LABEL[s.tipo_operacion] || (s.solicita_credito ? "Crédito" : "Contado")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {s.rfc ? `RFC: ${s.rfc} · ` : ""}Vendedor: {s.vendedor_nombre || "—"} · {fmtFecha(s.created_at)}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <Badge variant={ESTATUS_VARIANT[s.estatus] || "default"}>{ESTATUS_LABEL[s.estatus] || s.estatus}</Badge>
                      {isAdmin && s.estatus === "pendiente" && (
                        <>
                          <Button size="sm" onClick={() => navigate("/alta-clientes", { state: { solicitud: s } })} disabled={busy} data-testid={`registrar-${s.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Revisar y registrar</Button>
                          <Button size="sm" variant="outline" onClick={() => { setRechazar(s); setMotivo(""); }} disabled={busy} data-testid={`rechazar-${s.id}`}><XCircle className="h-4 w-4 mr-1" /> Rechazar</Button>
                        </>
                      )}
                      {isAdmin && s.estatus !== "pendiente" && (
                        <Button size="icon" variant="ghost" className="text-destructive" title="Eliminar" onClick={() => eliminar(s)} data-testid={`eliminar-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </div>

                  {abierto && (
                    <div className="mt-4 pt-3 border-t space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                        <div>
                          <h4 className="text-sm font-bold mb-1">Datos generales</h4>
                          <Detalle label="Nombre / Razón social" value={s.nombre_razon_social} />
                          <Detalle label="Contacto" value={s.nombre_contacto} />
                          <Detalle label="RFC" value={s.rfc} />
                          <Detalle label="Correo" value={s.email} />
                          <Detalle label="Teléfono" value={s.telefono} />
                          <Detalle label="WhatsApp" value={s.whatsapp} />
                          <h4 className="text-sm font-bold mb-1 mt-3">Dirección fiscal</h4>
                          <Detalle label="Calle" value={s.calle} />
                          <Detalle label="No. ext / int" value={[s.no_exterior, s.no_interior].filter(Boolean).join(" / ")} />
                          <Detalle label="Colonia" value={s.colonia} />
                          <Detalle label="C.P." value={s.codigo_postal} />
                          <Detalle label="Municipio" value={s.municipio_alcaldia} />
                          <Detalle label="Estado" value={s.estado} />
                        </div>
                        <div>
                          {s.solicita_credito && (
                            <>
                              <h4 className="text-sm font-bold mb-1">Crédito</h4>
                              <Detalle label="Tipo de persona" value={s.tipo_persona === "moral" ? "Persona moral" : "Persona física"} />
                              <Detalle label="Límite de crédito" value={m(s.limite_credito)} />
                              <Detalle label="Días de crédito" value={s.dias_credito ? `${s.dias_credito} días` : "—"} />
                            </>
                          )}
                          <h4 className="text-sm font-bold mb-1 mt-3">Datos de entrega</h4>
                          <Detalle label="Quién recibe" value={s.entrega_nombre_recibe} />
                          <Detalle label="Teléfono de entrega" value={s.entrega_telefono} />
                          <Detalle label="Calle y número" value={s.entrega_calle_numero} />
                          <Detalle label="Colonia" value={s.entrega_colonia} />
                          <Detalle label="Municipio" value={s.entrega_municipio} />
                          <Detalle label="Estado" value={s.entrega_estado} />
                          <Detalle label="Ubicación (Maps)" value={s.entrega_ubicacion_maps} />
                          {s.observaciones && (<><h4 className="text-sm font-bold mb-1 mt-3">Observaciones</h4><p className="text-sm whitespace-pre-wrap">{s.observaciones}</p></>)}
                          {s.estatus === "rechazada" && (<><h4 className="text-sm font-bold mb-1 mt-3 text-destructive">Motivo de rechazo</h4><p className="text-sm">{s.motivo_rechazo || "—"}</p></>)}
                          {s.revisado_por_nombre && (<p className="text-xs text-muted-foreground mt-3">Revisado por {s.revisado_por_nombre} · {fmtFecha(s.revisado_at)}</p>)}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-bold mb-2">Documentos {s.estatus === "aprobada" ? "(en expediente del cliente)" : "adjuntos"}</h4>
                        {s.estatus === "pendiente" && (() => {
                          const faltan = faltantesDocs(s, sdocs);
                          return faltan.length ? (
                            <p className="text-sm text-amber-600 inline-flex items-center gap-1 mb-2" data-testid={`faltan-${s.id}`}><AlertTriangle className="h-4 w-4" /> Faltan documentos: {faltan.join(", ")}</p>
                          ) : (
                            <p className="text-sm text-green-600 inline-flex items-center gap-1 mb-2"><CheckCircle2 className="h-4 w-4" /> Documentos completos</p>
                          );
                        })()}
                        {sdocs.length === 0 ? (
                          <p className="text-sm text-muted-foreground inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-amber-500" /> Sin documentos adjuntos.</p>
                        ) : (
                          <div className="space-y-1">
                            {sdocs.map((d) => (
                              <div key={d.id} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`sol-doc-${d.id}`}>
                                <span className="text-sm inline-flex items-center gap-2"><FileText className="h-4 w-4" /> <span className="font-medium">{d.tipo_documento}</span> <span className="text-muted-foreground">· {d.nombre_archivo}</span></span>
                                <div className="flex gap-1">
                                  <Button size="icon" variant="ghost" title="Ver" onClick={() => abrirBlob(d, false)} data-testid={`sol-doc-ver-${d.id}`}><Eye className="h-4 w-4" /></Button>
                                  <Button size="icon" variant="ghost" title="Descargar" onClick={() => abrirBlob(d, true)} data-testid={`sol-doc-desc-${d.id}`}><Download className="h-4 w-4" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!rechazar} onOpenChange={(o) => { if (!o) { setRechazar(null); setMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="rechazar-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar solicitud</DialogTitle>
            <DialogDescription>Opcional: indica el motivo para que el vendedor lo vea.</DialogDescription>
          </DialogHeader>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo del rechazo (opcional)" data-testid="rechazar-motivo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRechazar(null); setMotivo(""); }}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarRechazo} disabled={busy} data-testid="rechazar-confirmar">Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
